// Feldbelegung aus Datenquellen (Folie gestalten, M16): Text-, Laufschrift- und Kursplan-Felder aus einer Tabelle
// (bzw. Feed-Titeln) füllen; Feed-/Termine-/Tabellen-Bausteine einer Vorlage mit einer Quelle verbinden.
import type { DataBinding } from '@disploraq/layouts';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useDataSourceData, useDataSources, type DataSourceKind } from '../api/datasources';
import { can, useMe } from '../api/queries';
import { Icon } from './Icon';
import { hasFeature } from '../api/features';

const SEPARATORS = ['\n', ' · ', ' +++ ', ', '];

/** Schaltfläche "Aus Datenquelle füllen" unter einem Feld (nur mit Datenquellen-Recht). */
export function BindingSwitch({ kind, onBind }: { kind: 'text' | 'rows'; onBind: (b: DataBinding) => void }) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { data: sources = [] } = useDataSources({ enabled: can(me, 'datasources.read') && hasFeature(me, 'datasources') });
  const usable = sources.filter((s) => s.kind === 'table' || (kind === 'text' && s.kind === 'feed'));
  if (!can(me, 'datasources.read') || !hasFeature(me, 'datasources')) return null;
  return (
    <button type="button" className="btn sm ghost" style={{ alignSelf: 'flex-start' }} disabled={!usable.length} title={usable.length ? undefined : t('binding.none')}
      onClick={() => onBind({ type: 'data', source: usable[0]!.id })}>
      <Icon name="database" />{t('binding.fromSource')}
    </button>
  );
}

/** Einstellungen einer Feldbelegung; "Manuell eingeben" hebt sie auf (onChange(null)). */
export function BindingEditor({ kind, value, onChange }: { kind: 'text' | 'rows'; value: DataBinding; onChange: (b: DataBinding | null) => void }) {
  const { t } = useTranslation();
  const { data: sources = [] } = useDataSources();
  const usable = sources.filter((s) => s.kind === 'table' || (kind === 'text' && s.kind === 'feed'));
  const { data } = useDataSourceData(value.source || null);
  const cols = data?.data.kind === 'table' ? data.data.columns : [];
  const put = (p: Partial<DataBinding>) => onChange({ ...value, ...p });
  const colSelect = (label: string, cur: string | undefined, set: (v: string | undefined) => void, allowNone: boolean, fallback: number) => (
    <label className="field" key={label}>{label}
      <select value={cur ?? ''} onChange={(e) => set(e.target.value === '' ? undefined : e.target.value)}>
        <option value="">{cols[fallback] ? `${cols[fallback]!.label} (${t('binding.auto')})` : t('binding.empty')}</option>
        {allowNone && <option value={'\u0000'}>{t('binding.empty')}</option>}
        {cols.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
      </select>
    </label>
  );
  // "leer" wird als '' gespeichert (Spalte bewusst nicht anzeigen), "automatisch" als undefined
  const mapVal = (v: string | undefined) => (v === '' ? '\u0000' : v);
  const mapSet = (k: 'time' | 'course' | 'trainer' | 'room') => (v: string | undefined) => put({ map: { ...value.map, [k]: v === '\u0000' ? '' : v } });
  return (
    <div className="col binding" style={{ gap: 6 }} data-testid="binding-editor">
      <div className="row">
        <select className="grow" aria-label={t('binding.source')} value={value.source} onChange={(e) => onChange({ type: 'data', source: e.target.value })}>
          {usable.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <button type="button" className="btn sm ghost" onClick={() => onChange(null)}>{t('binding.manual')}</button>
      </div>
      {kind === 'rows' && cols.length > 0 && (
        <div className="two">
          {colSelect(t('studio.f.time'), mapVal(value.map?.time), mapSet('time'), true, 0)}
          {colSelect(t('studio.f.course'), mapVal(value.map?.course), mapSet('course'), true, 1)}
          {colSelect(t('gallery.trainer'), mapVal(value.map?.trainer), mapSet('trainer'), true, 2)}
          {colSelect(t('gallery.room'), mapVal(value.map?.room), mapSet('room'), true, 3)}
        </div>
      )}
      {kind === 'text' && (
        <div className="two">
          {cols.length > 0 && colSelect(t('binding.column'), value.column, (v) => put({ column: v }), false, 0)}
          <label className="field">{t('binding.separator')}
            <select value={value.separator ?? '\n'} onChange={(e) => put({ separator: e.target.value })}>
              {SEPARATORS.map((s) => <option key={s} value={s}>{t(`binding.sep.${SEPARATORS.indexOf(s)}`)}</option>)}
            </select>
          </label>
        </div>
      )}
      <label className="field">{t('binding.limit')}
        <input type="number" min={1} max={200} value={value.limit ?? ''} placeholder={kind === 'rows' ? '50' : '20'}
          onChange={(e) => put({ limit: e.target.value ? Math.max(1, Math.min(200, Number(e.target.value))) : undefined })} />
      </label>
      <span className="small muted">{t('binding.hint')} <Link to="/datasources">{t('datasources.manage')}</Link></span>
    </div>
  );
}

/** Quelle eines Feed-/Termine-/Tabellen-Bausteins einer Vorlage wählen (leer = Quelle der Vorlage). */
export function SourcePicker({ kind, value, onChange }: { kind: DataSourceKind; value: string; onChange: (id: string | null) => void }) {
  const { t } = useTranslation();
  const { data: sources = [] } = useDataSources();
  return (
    <select value={value} onChange={(e) => onChange(e.target.value || null)} aria-label={t('binding.source')}>
      <option value="">{t('binding.templateSource')}</option>
      {sources.filter((s) => s.kind === kind).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
    </select>
  );
}
